import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField, Button
} from '@one-thd/sui-atomic-components';
import {
  useLazyDataModel, params, string, arrayOf, shape, customType
} from '@thd-nucleus/data-sources';
import { localizeToProject, triggerAnalytics } from './helpers';
import {
  DELIVERY_ZIPCODE_UPDATED
} from './analytics';

export const DeliveryZipcodeForm = ({
  projectId,
  assortmentZipCode,
  assortmentZipId,
  preferredStore,
  onUpdate,
  onSubmitSuccess
}) => {
  const [assortmentZip, setAssortmentZip] = useState(assortmentZipCode);
  const [assortmentZipError, setAssortmentZipError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);

  const isValidZipCode = (zipCode) => {
    return zipCode?.length === 5 && /^\d+$/.test(zipCode);
  };

  const [
    fetchCityState, { data: cityAndStateData, error: cityAndStateError, loading }
  ] = useLazyDataModel('cityStateLookupByZipCode', {
    variables: {},
    context: { withAuth: true },
    fetchPolicy: 'cache-first',
  });

  const [updateProjectDetails] = useLazyDataModel('updateProject', {
    variables: {
      projectId
    },
    refetchQueries: [
      'projectDetails'
    ],
    context: { withAuth: true }
  });

  const handleOnChange = (__, event) => {
    const { value } = event.target;
    setAssortmentZip(value);
    setHasEdited(true);
    if (!isValidZipCode(value)) {
      setAssortmentZipError('Invalid Zip Code');
    }
  };

  const handleSubmit = async () => {
    if (assortmentZipError) {
      setShowErrors(true);
      return;
    }
    await updateProjectDetails({
      variables: {
        operationType: 'ADDRESS',
        addressInput: {
          addressType: 'ASSORTMENT_ZIP',
          zipCode: assortmentZip,
          id: assortmentZipId
        }
      }
    });
    if (preferredStore) {
      localizeToProject({
        assortmentZip,
        preferredStore,
        analyticsString: DELIVERY_ZIPCODE_UPDATED,
        onUpdate: () => {
          onUpdate();
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        }
      });
    } else {
      triggerAnalytics(DELIVERY_ZIPCODE_UPDATED, {});
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    }
  };

  useEffect(() => {
    if (isValidZipCode(assortmentZip) && hasEdited) {
      fetchCityState({
        variables: {
          zipCode: assortmentZip
        }
      });
    }
  }, [assortmentZip]);

  // Update assortmentZip state when assortmentZipCode prop changes
  useEffect(() => {
    if (assortmentZipCode && !hasEdited) {
      setAssortmentZip(assortmentZipCode);
    }
  }, [assortmentZipCode, hasEdited]);

  useEffect(() => {
    const cityAndStateArray = cityAndStateData?.cityStateLookupByZipCode?.postalDetailsList || [];
    if (cityAndStateArray.length && !loading && !cityAndStateError && isValidZipCode(assortmentZip)) {
      setAssortmentZipError('');
    } else {
      setAssortmentZipError('Invalid Zip Code');
    }
  }, [cityAndStateData, loading, cityAndStateError, assortmentZip]);

  return (
    <div className="sui-flex sui-flex-col sui-gap-3 sui-m-4">
      <TextField
        required
        id="project-zip"
        label="Delivery Zip"
        type="text"
        value={assortmentZip || ''}
        placeholder="Enter Zip Code"
        fullWidth
        onChange={(event) => { handleOnChange('assortmentZip', event); }}
        status={showErrors && assortmentZipError ? 'error' : null}
        statusMessage={showErrors ? assortmentZipError : ''}
        InputProps={{
          inputProps: {
            'data-testid': 'project-zip-input',
            maxLength: '5'
          }
        }}
      />
      <Button data-testid="delivery-zip-update-button" onClick={handleSubmit} fullWidth>Update</Button>
    </div>
  );
};

DeliveryZipcodeForm.displayName = 'DeliveryZipcodeForm';

DeliveryZipcodeForm.propTypes = {
  projectId: PropTypes.string,
  assortmentZipCode: PropTypes.string,
  assortmentZipId: PropTypes.string,
  preferredStore: PropTypes.string,
  onUpdate: PropTypes.func,
  onSubmitSuccess: PropTypes.func
};

DeliveryZipcodeForm.defaultProps = {
  projectId: '',
  assortmentZipCode: '',
  assortmentZipId: '',
  preferredStore: '',
  onUpdate: () => {},
  onSubmitSuccess: null
};

DeliveryZipcodeForm.dataModel = {
  cityStateLookupByZipCode: params({
    zipCode: string().isRequired(),
  }).shape({
    postalDetailsList: arrayOf(
      shape({
        city: string(),
        state: string()
      })
    )
  }),
  updateProject: params({
    projectId: string().isRequired(),
    operationType: customType('OperationType').enum(['STATUS', 'INFO', 'ADDRESS']).isRequired(),
    addressInput: customType('AddressUpdate').shape({
      id: string().isRequired(),
      addressType: customType('AddressType').enum(['ASSORTMENT_ZIP', 'FULFILLMENT', 'JOB_SITE']).isRequired(),
      zipCode: string().isRequired(),
    })
  }).mutation().shape({
    id: string()
  })
};
